import {
  Component,
  createContext,
  useContext,
  useId,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { T, useGT } from 'gt-react';
import {
  chartDetailForSize,
  type ChartDetail,
  type ChartSize,
} from '@erp/viz/fit';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip as UITooltip,
  TooltipTrigger as UITooltipTrigger,
  TooltipContent as UITooltipContent,
} from '@/components/ui/tooltip';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { StaleChunkRecovery } from '@/components/common/StaleChunkRecovery';
import { isQuietStaleChunk } from '@/lib/stale-chunk-error';
import { useReportDataset, useReportDatasets } from '../ReportDataContext';
import type { ComponentTypeDefinition } from '../registry';
import type { BaseComponentConfig } from '../types';
import { NO_AXIS } from './chartConstants';

// ── Chart states ──────────────────────────────────────────────────────────────

/**
 * How a component's settings open where it is shown: selecting it (a Report
 * Builder sidebar), its gear icon (a settings popover), or not at all (a
 * read-only view). Set per canvas item; empty states name the one that works.
 */
export type ConfigureAffordance = 'select' | 'gear' | 'none';

export const ConfigureAffordanceContext =
  createContext<ConfigureAffordance>('none');

function SetUpHint() {
  const affordance = useContext(ConfigureAffordanceContext);
  if (affordance === 'select') return <T>Select it to set it up</T>;
  if (affordance === 'gear') return <T>Set it up with the gear icon</T>;
  return <T>Not set up yet</T>;
}

export function ChartEmptyState({
  hasData,
  icon,
}: {
  hasData: boolean;
  icon: React.ReactNode;
}) {
  const { emptyHint } = useReportDatasets();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-4">
      {icon}
      <p className="text-center text-xs text-muted-foreground">
        {!hasData ? (emptyHint ?? <T>No data available yet</T>) : <SetUpHint />}
      </p>
    </div>
  );
}

export function ChartIssueIcon({ messages }: { messages: string[] }) {
  return (
    <UITooltip>
      <UITooltipTrigger asChild>
        <span className="inline-flex cursor-default text-amber-500">
          <WarningCircleIcon className="h-4 w-4" />
        </span>
      </UITooltipTrigger>
      <UITooltipContent>
        <p className="max-w-xs whitespace-pre-wrap break-words text-xs">
          {messages.join('\n')}
        </p>
      </UITooltipContent>
    </UITooltip>
  );
}

function ChartErrorState({ message }: { message: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <ChartIssueIcon messages={[message]} />
    </div>
  );
}

interface ChartErrorBoundaryProps {
  children: ReactNode;
  resetKey: string;
}

interface ChartErrorBoundaryState {
  error: Error | null;
  lastKey: string;
}

export class ChartErrorBoundary extends Component<
  ChartErrorBoundaryProps,
  ChartErrorBoundaryState
> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { error: null, lastKey: props.resetKey };
  }

  static getDerivedStateFromError(
    error: Error,
  ): Partial<ChartErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ChartErrorBoundaryProps,
    state: ChartErrorBoundaryState,
  ): Partial<ChartErrorBoundaryState> | null {
    if (props.resetKey !== state.lastKey) {
      return { error: null, lastKey: props.resetKey };
    }
    return null;
  }

  render() {
    if (this.state.error) {
      if (isQuietStaleChunk(this.state.error)) {
        return <StaleChunkRecovery />;
      }
      return <ChartErrorState message={this.state.error.message} />;
    }
    return this.props.children;
  }
}

// ── Config form primitives ────────────────────────────────────────────────────

export function ColumnSelect({
  value,
  columns,
  placeholder,
  onChange,
}: {
  value: string | null;
  columns: { key: string; label: string }[];
  placeholder: string;
  onChange: (key: string | null) => void;
}) {
  return (
    <Select
      value={value ?? NO_AXIS}
      onValueChange={(v) => onChange(v === NO_AXIS ? null : v)}
    >
      <ConfigSelectTrigger>
        <SelectValue placeholder={placeholder} />
      </ConfigSelectTrigger>
      <SelectContent>
        <SelectItem value={NO_AXIS}>
          <span className="text-muted-foreground">
            <T>None</T>
          </span>
        </SelectItem>
        {columns.map((col) => (
          <SelectItem key={col.key} value={col.key}>
            {col.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The id of the enclosing ConfigRow's label, for the control it names. */
const ConfigRowLabelContext = createContext<string | undefined>(undefined);

/**
 * A settings row: a label and its control. A Select trigger isn't a labelable
 * element, so `<Label htmlFor>` can't name it; the row's dropdowns use
 * ConfigSelectTrigger, which takes its name from the label by id.
 */
export function ConfigRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  const labelId = useId();
  return (
    <div className="space-y-1.5">
      <Label id={labelId} htmlFor={htmlFor}>
        {label}
      </Label>
      <ConfigRowLabelContext.Provider value={labelId}>
        {children}
      </ConfigRowLabelContext.Provider>
    </div>
  );
}

/** A SelectTrigger named by the ConfigRow it sits in, unless it names itself. */
export function ConfigSelectTrigger(
  props: ComponentProps<typeof SelectTrigger>,
) {
  const labelId = useContext(ConfigRowLabelContext);
  const named = props['aria-label'] ?? props['aria-labelledby'];
  return (
    <SelectTrigger
      aria-labelledby={named === undefined ? labelId : undefined}
      {...props}
    />
  );
}

export function ToggleRow({
  label,
  id,
  checked,
  onCheckedChange,
}: {
  label: string;
  id: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <Label htmlFor={id}>{label}</Label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

/** A clickable color square backed by a hidden native color input. */
export function ColorSwatch({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (color: string) => void;
  label: string;
}) {
  return (
    <label
      className="relative block h-7 w-7 shrink-0 cursor-pointer rounded border border-border shadow-sm"
      style={{ backgroundColor: value }}
      aria-label={label}
      title={value}
    >
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </label>
  );
}

/**
 * Dataset picker row for chart config forms. Rendered only when the surface
 * provides multiple datasets (single-dataset surfaces like the query builder
 * keep their existing form layout). Switching datasets calls
 * `onDatasetChange`, which must reset the component's column bindings since
 * columns differ across datasets.
 */
export function DatasetSelectRow({
  component,
  onDatasetChange,
}: {
  component: BaseComponentConfig;
  onDatasetChange: (datasetKey: string) => void;
}) {
  const gt = useGT();
  const { datasets, defaultDatasetKey } = useReportDatasets();
  if (datasets.length <= 1) return null;
  const current = component.datasetKey ?? defaultDatasetKey;
  return (
    <ConfigRow label={gt('Data source')}>
      <Select value={current} onValueChange={onDatasetChange}>
        <ConfigSelectTrigger>
          <SelectValue />
        </ConfigSelectTrigger>
        <SelectContent>
          {datasets.map((d) => (
            <SelectItem key={d.key} value={d.key}>
              {d.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </ConfigRow>
  );
}

// ── Chart fit ─────────────────────────────────────────────────────────────────

/** The padding every chart renderer draws inside its grid cell (`p-2`). */
const CHART_PADDING = 16;

export interface ChartFit extends ChartSize {
  /** How much of a chart to draw at this size (`chartDetailForSize`). */
  detail: ChartDetail;
}

const FULL_FIT: ChartFit = {
  width: 0,
  height: 0,
  detail: chartDetailForSize({ width: 0, height: 0 }),
};

/** The size a chart has to draw in, set by its canvas item. */
export const ChartFitContext = createContext<ChartFit>(FULL_FIT);

/** The chart fit for a component body of `size` (its padding taken off). */
export function chartFitFor(size: ChartSize): ChartFit {
  const inner = {
    width: Math.max(0, size.width - CHART_PADDING),
    height: Math.max(0, size.height - CHART_PADDING),
  };
  // An unmeasured body stays "unmeasured" rather than a 0×0 chart.
  const measured = size.width > 0 && size.height > 0;
  return measured ? { ...inner, detail: chartDetailForSize(inner) } : FULL_FIT;
}

export function useChartFit(): ChartFit {
  return useContext(ChartFitContext);
}

/** In place of a chart its cell is too small to draw legibly. */
export function ChartTooSmall() {
  return (
    <div className="flex h-full min-h-0 items-center justify-center overflow-hidden p-1 text-center">
      <p className="text-[11px] leading-tight text-muted-foreground">
        <T>Too small to draw — enlarge</T>
      </p>
    </div>
  );
}

/**
 * A built-in chart's body: the chart once its columns are chosen and its
 * dataset loaded (or a "too small" note while its cell can't fit it), else
 * the empty state saying which of the two is missing.
 */
export function ChartFrame({
  ready,
  hasData,
  icon,
  children,
}: {
  ready: boolean;
  hasData: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const { detail } = useChartFit();
  let body: React.ReactNode = children;
  if (!ready) body = <ChartEmptyState hasData={hasData} icon={icon} />;
  else if (!detail.drawable) body = <ChartTooSmall />;
  return <div className="flex min-h-0 min-w-0 flex-1 flex-col p-2">{body}</div>;
}

/**
 * The data source and category-and-value column pickers of a Bar or Line
 * chart: categories from its text columns, values from its Numbers. A new
 * data source clears both columns.
 */
export function CategoryValueFields<
  C extends BaseComponentConfig & {
    xAxisKey: string | null;
    yAxisKey: string | null;
  },
>({
  component,
  onUpdate,
  xLabel,
  yLabel,
}: {
  component: C;
  onUpdate: (updated: C) => void;
  xLabel: string;
  yLabel: string;
}) {
  const gt = useGT();
  const dataset = useReportDataset(component.datasetKey);
  const columns = dataset?.columns ?? [];
  const stringColumns = columns.filter((c) => c.dataType !== 'number');
  const numericColumns = columns.filter((c) => c.dataType === 'number');
  return (
    <>
      <DatasetSelectRow
        component={component}
        onDatasetChange={(datasetKey) =>
          onUpdate({ ...component, datasetKey, xAxisKey: null, yAxisKey: null })
        }
      />
      <ConfigRow label={xLabel}>
        <ColumnSelect
          value={component.xAxisKey}
          columns={
            stringColumns.length > 0
              ? stringColumns
              : [{ key: NO_AXIS, label: gt('No string columns in result') }]
          }
          placeholder={gt('X-axis column…')}
          onChange={(k) => onUpdate({ ...component, xAxisKey: k })}
        />
      </ConfigRow>
      <ConfigRow label={yLabel}>
        <ColumnSelect
          value={component.yAxisKey}
          columns={
            numericColumns.length > 0
              ? numericColumns
              : [{ key: NO_AXIS, label: gt('No numeric columns in result') }]
          }
          placeholder={gt('Y-axis column…')}
          onChange={(k) => onUpdate({ ...component, yAxisKey: k })}
        />
      </ConfigRow>
    </>
  );
}

/**
 * An X/Y chart's dataset and columns: whether it can draw, and each axis's
 * column ('' until chosen).
 */
export function useXYChart(component: {
  datasetKey?: string;
  xAxisKey: string | null;
  yAxisKey: string | null;
}) {
  const dataset = useReportDataset(component.datasetKey);
  return {
    dataset,
    ready: chartReady(dataset, component.xAxisKey, component.yAxisKey),
    xKey: component.xAxisKey ?? '',
    yKey: component.yAxisKey ?? '',
    // Each axis's column label, for its title and the tooltip.
    xLabel: columnLabelOf(dataset?.columns ?? [], component.xAxisKey),
    yLabel: columnLabelOf(dataset?.columns ?? [], component.yAxisKey),
  };
}

/** Whether a chart can draw: its dataset is loaded and each column chosen. */
export function chartReady(
  dataset: unknown,
  ...keys: readonly (string | null)[]
): boolean {
  return dataset !== null && keys.every((key) => key !== null);
}

/** A column's label among `columns`, or its key when it has none. */
export function columnLabelOf(
  columns: readonly { key: string; label: string }[],
  key: string | null,
): string {
  return columns.find((column) => column.key === key)?.label ?? key ?? '';
}

/**
 * A chart's default title from the columns it plots: "{value} by {category}",
 * or whichever one is chosen, or `fallback` (its type's label) before either.
 */
export function valueByCategoryTitle<C extends BaseComponentConfig>(
  categoryField: keyof C & string,
  valueField: keyof C & string,
  fallback: string,
): NonNullable<ComponentTypeDefinition<C>['defaultTitle']> {
  return (component, { dataset, gt }) => {
    const label = (field: keyof C & string) => {
      const key = component[field];
      if (typeof key !== 'string' || key.length === 0 || key === NO_AXIS)
        return null;
      return (
        dataset?.columns.find((column) => column.key === key)?.label ?? key
      );
    };
    const value = label(valueField);
    const category = label(categoryField);
    if (value && category)
      return gt('{value} by {category}', {
        value,
        category,
        context: 'report chart default title',
      });
    return value ?? category ?? fallback;
  };
}
